import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Users, UserPlus, Trash2, Shield, Mail, KeyRound, Loader2, Crown } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const ROLE_BADGE = {
  owner: { label: "Owner", cls: "bg-amber-950/80 text-amber-400 border-amber-800/60", icon: Crown },
  admin: { label: "Admin", cls: "bg-cyan-950/80 text-cyan-400 border-cyan-800/60", icon: Shield },
  employee: { label: "Employee", cls: "bg-slate-800/80 text-slate-300 border-slate-700/60", icon: Users },
};

export default function Team() {
  const qc = useQueryClient();
  const { data: members = [], isLoading } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/team")).data });
  const [form, setForm] = useState({ email: "", name: "", role: "employee", method: "google", password: "" });
  const [adding, setAdding] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const add = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post("/team", form);
      toast.success(`${form.email} added to the team`);
      setForm({ email: "", name: "", role: "employee", method: "google", password: "" });
      qc.invalidateQueries({ queryKey: ["team"] });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAdding(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.email} from the team?`)) return;
    try { await api.delete(`/team/${m.id}`); toast.success("Member removed"); qc.invalidateQueries({ queryKey: ["team"] }); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6">
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-100">Team</h1>
        <p className="text-sm text-slate-400 mt-1">Add admins and employees to help manage sales. They share your inventory and their sales are tracked by name.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <form onSubmit={add} className="lg:col-span-2 rounded-2xl border p-5 h-fit" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }} data-testid="team-add-form">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400 mb-4"><UserPlus className="h-4 w-4" /> Add a member</div>
          <Field label="Email">
            <input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="team-email-input"
              placeholder="teammate@email.com" className="w-full px-3 py-2.5 rounded-lg border text-sm text-slate-100 outline-none focus:ring-2 focus:ring-amber-500/50" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }} />
          </Field>
          <Field label="Name (optional)">
            <input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="team-name-input"
              className="w-full px-3 py-2.5 rounded-lg border text-sm text-slate-100 outline-none focus:ring-2 focus:ring-amber-500/50" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Role">
              <select value={form.role} onChange={(e) => set("role", e.target.value)} data-testid="team-role-select"
                className="w-full px-3 py-2.5 rounded-lg border text-sm text-slate-100 outline-none" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
                <option value="employee" style={{ background: "#0D1017" }}>Employee</option>
                <option value="admin" style={{ background: "#0D1017" }}>Admin</option>
              </select>
            </Field>
            <Field label="Sign-in">
              <select value={form.method} onChange={(e) => set("method", e.target.value)} data-testid="team-method-select"
                className="w-full px-3 py-2.5 rounded-lg border text-sm text-slate-100 outline-none" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
                <option value="google" style={{ background: "#0D1017" }}>Google</option>
                <option value="password" style={{ background: "#0D1017" }}>Email + password</option>
              </select>
            </Field>
          </div>
          {form.method === "password" ? (
            <Field label="Temporary password">
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
                <KeyRound className="h-4 w-4 text-slate-500" />
                <input type="text" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="team-password-input"
                  placeholder="min 6 characters" className="w-full bg-transparent outline-none text-sm text-slate-100" />
              </div>
            </Field>
          ) : (
            <p className="text-[11px] text-slate-500 flex items-center gap-1.5 mt-1"><Mail className="h-3.5 w-3.5" /> They sign in with "Continue with Google" using this exact email.</p>
          )}
          <button type="submit" disabled={adding} data-testid="team-add-button"
            className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-amber-500 text-[#1a1205] font-semibold text-sm hover:bg-amber-400 disabled:opacity-60">
            {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Add member
          </button>
        </form>

        <div className="lg:col-span-3 rounded-2xl border overflow-hidden" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
          <div className="px-5 py-4 border-b flex items-center gap-2" style={{ borderColor: "var(--pk-border)" }}>
            <Users className="h-4 w-4 text-amber-400" /><h2 className="font-heading font-bold text-slate-100">Members ({members.length})</h2>
          </div>
          {isLoading ? <div className="py-16 text-center text-slate-500"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div> : (
            <div className="divide-y" style={{ borderColor: "var(--pk-border-subtle)" }}>
              {members.map((m) => {
                const b = ROLE_BADGE[m.role] || ROLE_BADGE.employee;
                return (
                  <div key={m.id} data-testid="team-member-row" className="flex items-center gap-3 px-5 py-3">
                    <div className="h-9 w-9 rounded-full bg-amber-500/15 text-amber-400 flex items-center justify-center text-sm font-semibold">{(m.name || m.email)[0]?.toUpperCase()}</div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-100 truncate">{m.name || m.email.split("@")[0]} {m.is_self && <span className="text-[11px] text-slate-500">(you)</span>}</div>
                      <div className="text-[11px] text-slate-500 truncate">{m.email} · {m.auth_provider === "google" ? "Google" : "Password"}</div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold border inline-flex items-center gap-1 ${b.cls}`}><b.icon className="h-3 w-3" />{b.label}</span>
                    {!m.is_owner && !m.is_self && (
                      <button onClick={() => remove(m)} data-testid="team-remove-button" className="h-8 w-8 rounded flex items-center justify-center text-slate-400 hover:text-rose-400 hover:bg-rose-500/10"><Trash2 className="h-4 w-4" /></button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mb-3">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
